import { createReducer } from '@reduxjs/toolkit';

import { getSinkSystemId } from 'Internals/pipes';
import { OutboundMap } from 'Types/common.types';
import type { Pipe, PipeResponse } from 'Types/pipes.types';
import { disconnected } from '../thunks/subscriptions.thunks';
import {
  pipeAdded,
  pipeUpdated,
  pipeLoaded,
  pipesLoadAllFinished,
  pipesRemoved,
} from '../thunks/pipes.thunks';
import { systemRemoved } from '../thunks/systems.thunks';

type OutboundsState = OutboundMap;

export const initialState: OutboundsState = {};

function setOutbound(pipe: Pipe | PipeResponse, state: OutboundsState) {
  const sinkSystemId = getSinkSystemId(pipe);
  if (sinkSystemId) {
    if (!state[sinkSystemId]) {
      state[sinkSystemId] = { [pipe._id]: true };
    } else {
      state[sinkSystemId][pipe._id] = true;
    }
  }
}

/*
Note: We are using the builder callback for creating a typesafe reducer.
See https://redux-toolkit.js.org/usage/usage-with-typescript#building-type-safe-reducer-argument-objects
*/
export const outboundsReducer = createReducer(initialState, (builder) =>
  builder
    .addCase(disconnected, () => initialState)
    .addCase(pipeAdded, (state, action) => {
      setOutbound(action.payload, state);
    })
    .addCase(pipeUpdated, (state, action) => {
      setOutbound(action.payload.pipe, state);
    })
    .addCase(pipeLoaded, (state, action) => {
      setOutbound(action.payload.pipe, state);
    })
    .addCase(pipesLoadAllFinished, (state, action) => {
      const newState = {};
      for (let i = 0; i < action.payload.length; i++) {
        setOutbound(action.payload[i], newState);
      }
      return newState;
    })
    .addCase(pipesRemoved, (state, action) => {
      action.payload.forEach((p) => {
        if (p.sinkSystemId && state[p.sinkSystemId]) {
          delete state[p.sinkSystemId][p.id];
        }
      });
    })
    .addCase(systemRemoved, (state, action) => {
      action.payload.forEach((systemId) => {
        if (state[systemId]) {
          delete state[systemId];
        }
      });
    })
);
